import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ArrowLeftRight, Wrench, PackageX, FileText, ExternalLink } from "lucide-react";
import PesquisaBanner from "../../components/PesquisaBanner";

const STATUS_BADGE = {
  ativo: { label: "Locado", color: "bg-blue-100 text-blue-700 border-blue-200", dot: "#2563eb" },
  troca: { label: "Em troca", color: "bg-orange-100 text-orange-700 border-orange-200", dot: "#ff6b23" },
  manutencao: { label: "Em atendimento", color: "bg-orange-100 text-orange-700 border-orange-200", dot: "#ff6b23" },
  extraviado: { label: "Extraviado", color: "bg-red-100 text-red-700 border-red-200", dot: "#dc2626" },
  devolvido: { label: "Devolvido", color: "bg-gray-100 text-gray-700 border-gray-200", dot: "#6b7280" },
};

const TIPO_TICKET_LABEL = { troca: "Troca", manutencao: "Manutenção", devolucao: "Devolução", aditivo: "Aditivo" };
const STATUS_TICKET_LABEL = {
  aberto: "Aberto", em_atendimento: "Em andamento", resolvido: "Resolvido",
  fechado: "Fechado", cancelado: "Cancelado",
};

export default function ClienteHome() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [frota, setFrota] = useState([]);
  const [clienteLoc, setClienteLoc] = useState(null);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    api.get("/portal/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/cliente/minha-frota")
      .then((r) => {
        const items = r.data?.items || [];
        if (r.data?.cliente) setClienteLoc(r.data.cliente);
        if (items.length > 0) {
          setFrota(items.map((e) => ({
            id: e.id,
            modelo_nome: e.propriedade ? String(e.propriedade).toUpperCase() : "EQUIPAMENTO",
            numero_serie: e.serial,
            status_visual: e.status === "com_cliente" || e.status === "locado" ? "ativo" : (e.status || "ativo"),
          })));
        } else {
          api.get("/portal/minha-frota").then((r2) => setFrota(r2.data.unidades || [])).catch(() => {});
        }
      })
      .catch(() => {
        api.get("/portal/minha-frota").then((r) => setFrota(r.data.unidades || [])).catch(() => {});
      });
    api.get("/portal/tickets")
      .then((r) => {
        const all = r.data.tickets || [];
        // Excluir cancelados/fechados dos "Chamados recentes" — visual cleaner
        const ativos = all.filter((t) => !["cancelado", "fechado"].includes(t.status));
        setTickets(ativos.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  const empresa = clienteLoc?.nome_fantasia || me?.cliente?.nome || me?.cliente?.nome_fantasia || "Cliente";

  return (
    <div className="space-y-6" data-testid="cliente-home">
      {/* Banner pesquisa satisfação (aparece se houver pesquisa pendente) */}
      <PesquisaBanner />

      {/* Banner boas-vindas — MENSAGEM OFICIAL FIXA (texto exato do brief) */}
      <Card
        className="p-6 sm:p-8 border-l-4"
        style={{ borderLeftColor: "#ff6b23" }}
        data-testid="cliente-welcome-banner"
      >
        <h1
          className="text-2xl sm:text-3xl font-display font-bold mb-4"
          style={{ color: "#0f1923" }}
          data-testid="cliente-welcome-title"
        >
          Olá, <span style={{ color: "#ff6b23" }}>{empresa}</span>!
        </h1>
        <div className="space-y-3 text-sm leading-relaxed text-slate-700 max-w-3xl" data-testid="cliente-welcome-message">
          <p>Seja muito bem-vindo à Locar Ativo Brasil.</p>
          <p>
            É uma honra tê-lo como nosso cliente. Sabemos que confiar a gestão da sua frota a um
            parceiro é uma decisão estratégica — e nos comprometemos a estar sempre ao seu lado,
            com agilidade, transparência e o melhor serviço do mercado.
          </p>
          <p>
            Aqui você acompanha sua frota em tempo real, abre solicitações, acessa seus documentos
            e se mantém sempre conectado com nossa equipe.
          </p>
          <p>Qualquer dúvida ou sugestão, estamos à disposição.</p>
        </div>
        <div className="mt-5 pt-4 border-t border-slate-100">
          <div className="text-sm font-semibold" style={{ color: "#0f1923" }}>
            Carlos Eduardo Medeiros
          </div>
          <div className="text-xs text-slate-500">Diretor — Locar Ativo Brasil</div>
        </div>
      </Card>

      {/* Frota */}
      <section data-testid="cliente-home-frota">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-display font-bold" style={{ color: "#0f1923" }}>
            Sua frota atual ({frota.length} {frota.length === 1 ? "equipamento" : "equipamentos"})
          </h2>
          <Button variant="ghost" size="sm" onClick={() => nav("/cliente/meu_perfil")} data-testid="ver-meu-perfil-frota" className="text-orange-600 hover:text-orange-700">
            Ver todos <ExternalLink size={12} className="ml-1" />
          </Button>
        </div>
        {frota.length === 0 ? (
          <Card className="p-6 text-center text-sm text-gray-500" data-testid="cliente-frota-vazia">
            Você ainda não possui equipamentos vinculados à sua frota. Fale com seu consultor.
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {frota.slice(0, 6).map((u) => {
              const st = STATUS_BADGE[u.status_visual] || STATUS_BADGE.ativo;
              return (
                <Card key={u.id} className="p-4 hover:shadow-md transition" data-testid={`cliente-frota-card-${u.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold" style={{ color: "#0f1923" }}>{u.modelo_nome || "Equipamento"}</div>
                      <div className="text-xs text-gray-500 mt-0.5">#{u.numero_serie}</div>
                    </div>
                    <Badge variant="outline" className={`shrink-0 ${st.color}`}>
                      <span className="w-1.5 h-1.5 rounded-full mr-1.5 inline-block" style={{ background: st.dot }} />
                      {st.label}
                    </Badge>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* Ações rápidas */}
      <section>
        <h2 className="text-lg font-display font-bold mb-3" style={{ color: "#0f1923" }}>Ações rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <ActionCard testid="acao-troca"      icon={<ArrowLeftRight size={20} />} title="Solicitar troca"     subtitle="Da sua frota atual"     onClick={() => nav("/cliente/chamados/troca")} />
          <ActionCard testid="acao-aditivo"    icon={<FileText size={20} />}       title="Solicitar aditivo"   subtitle="Adicionar equipamentos" onClick={() => nav("/cliente/chamados/aditivo")} />
          <ActionCard testid="acao-devolucao"  icon={<PackageX size={20} />}       title="Solicitar devolução" subtitle="Encerrar locação"       onClick={() => nav("/cliente/chamados/devolucao")} />
          <ActionCard testid="acao-manutencao" icon={<Wrench size={20} />}         title="Reportar defeito"    subtitle="Manutenção corretiva"   onClick={() => nav("/cliente/chamados/manutencao")} />
        </div>
      </section>

      {/* Chamados recentes */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-display font-bold" style={{ color: "#0f1923" }}>Chamados recentes</h2>
          <Button variant="ghost" size="sm" onClick={() => nav("/cliente/chamados")} data-testid="ver-todos-chamados" className="text-orange-600 hover:text-orange-700">
            Ver todos <ExternalLink size={12} className="ml-1" />
          </Button>
        </div>
        {tickets.length === 0 ? (
          <Card className="p-6 text-center text-sm text-gray-500" data-testid="cliente-tickets-vazio">
            Você ainda não abriu chamados.
          </Card>
        ) : (
          <div className="space-y-2" data-testid="cliente-tickets-recentes">
            {tickets.map((t) => (
              <Card key={t.id} className="p-3 sm:p-4 hover:shadow-md cursor-pointer transition" onClick={() => nav("/cliente/chamados")} data-testid={`cliente-ticket-recente-${t.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold" style={{ color: "#0f1923" }}>
                      {TIPO_TICKET_LABEL[t.tipo] || t.tipo} · {t.modelo_nome || "—"}
                    </div>
                    <div className="text-xs text-gray-500">
                      #{t.id.slice(0, 6).toUpperCase()} · {new Date(t.criado_em).toLocaleDateString("pt-BR")}
                    </div>
                  </div>
                  <Badge variant="outline" className="shrink-0 capitalize">
                    {STATUS_TICKET_LABEL[t.status] || t.status}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const ActionCard = ({ icon, title, subtitle, onClick, testid }) => (
  <Card onClick={onClick} data-testid={testid} className="cursor-pointer p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all border-2 hover:border-orange-300">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: "#ff6b23" }}>{icon}</div>
      <div className="min-w-0">
        <div className="font-semibold" style={{ color: "#0f1923" }}>{title}</div>
        <div className="text-xs text-gray-500">{subtitle}</div>
      </div>
    </div>
  </Card>
);
